import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Plus, Trash2, AlertCircle, Check, Info } from 'lucide-react';
import { user } from '../api';

const MAX_DOMAINS = 2;

const DomainsPage = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      const data = await user.getDomains();
      setDomains(data.domains);
    } catch (error) {
      console.error('Failed to load domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;

    setError('');
    setSuccess('');
    setAdding(true);

    try {
      const data = await user.addDomain(newDomain.trim());
      setDomains((prev) => [...prev, data.domain]);
      setNewDomain('');
      setSuccess(`Domain "${data.domain.domain}" added successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveDomain = async (id, domain) => {
    if (!window.confirm(`Remove "${domain}"? You can add a different domain after removing.`)) return;

    try {
      await user.removeDomain(id);
      setDomains(domains.filter((d) => d.id !== id));
      setSuccess(`Domain "${domain}" removed`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-white mb-2">Allowed Domain</h1>
        <p className="text-white/60">
          You can whitelist up to {MAX_DOMAINS} domains per account. Requests from those domains will be accepted by your VTO widget.
        </p>
      </motion.div>

      {/* Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-blue-900/20 rounded-xl p-4 border border-blue-500/20 flex items-start gap-3 mb-6"
      >
        <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium text-blue-300 mb-1">Localhost is always allowed</h3>
          <p className="text-sm text-blue-300/70">
            For development, localhost and 127.0.0.1 are automatically allowed without needing to add them here.
          </p>
        </div>
      </motion.div>

      {/* Add Domain Form - shown until limit is reached */}
      {domains.length < MAX_DOMAINS && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#111118] rounded-2xl p-6 border border-white/5 mb-6"
        >
          <h2 className="text-lg font-bold text-white mb-4">Add Domain</h2>
          <form onSubmit={handleAddDomain} className="flex gap-3">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
              className="flex-1 px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50 transition-colors"
            />
            <button
              type="submit"
              disabled={adding || !newDomain.trim()}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {adding ? 'Adding...' : 'Add'}
            </button>
          </form>
          <p className="text-sm text-white/40 mt-3">
            Enter your domain without http:// or https:// (e.g., myshop.com)
          </p>
        </motion.div>
      )}

      {/* Status Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-900/20 rounded-xl p-4 border border-red-500/20 flex items-center gap-3 mb-6"
          >
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-300">{error}</p>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-emerald-900/20 rounded-xl p-4 border border-emerald-500/20 flex items-center gap-3 mb-6"
          >
            <Check className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-300">{success}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Domains List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-[#111118] rounded-2xl border border-white/5 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-white/5">
          <h2 className="text-lg font-bold text-white">
            Your Domains
          </h2>
        </div>

        {domains.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-white/20" />
            </div>
            <p className="text-white/60 mb-2">No domains added yet</p>
            <p className="text-sm text-white/40">
              Add your production domain(s) above to allow widget requests from your site.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {domains.map((domain, index) => (
              <motion.li
                key={domain.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="px-6 py-4 flex items-center justify-between hover:bg-white/2 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{domain.domain}</p>
                    <p className="text-sm text-white/40">
                      Added {new Date(domain.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-emerald-400 font-medium">Activated</span>
                  <button
                    onClick={() => handleRemoveDomain(domain.id, domain.domain)}
                    className="p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Remove domain"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </motion.li>
            ))}
          </ul>
        )}
        {domains.length >= MAX_DOMAINS && (
          <div className="px-6 py-3 border-t border-white/5 bg-white/[0.02]">
            <p className="text-sm text-white/40">
              Domain limit reached ({MAX_DOMAINS}/{MAX_DOMAINS}). Remove one domain to add a different one.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default DomainsPage;

